import { chatIdMutation } from "@/shared/api/queries"

import { groupSettingsApi } from "./get-settings"

import { sanitizeGroupSettingsPatch } from "../lib"

import type {
  SetGroupDefaultCardStyleRequest,
  SetGroupDefaultCardStyleResponse,
  UpdateGroupSettingsRequest,
  UpdateGroupSettingsResponse,
  UpdateMediaRestrictionsRequest,
  UpdateMediaRestrictionsResponse,
  UpdateNotificationPreferencesRequest,
  UpdateNotificationPreferencesResponse
} from "./types"

export const updateGroupSettingsApi = groupSettingsApi
  .enhanceEndpoints({ addTagTypes: ["UserCardStyle", "CardStyles"] })
  .injectEndpoints({
    endpoints: builder => ({
      updateGroupSettings: builder.mutation<UpdateGroupSettingsResponse, UpdateGroupSettingsRequest>({
        queryFn: chatIdMutation<UpdateGroupSettingsRequest, UpdateGroupSettingsResponse>((chatId, arg) => ({
          url: `/api/v1/group/settings?chat_id=${chatId}`,
          method: "PATCH",
          body: sanitizeGroupSettingsPatch(arg)
        })),
        invalidatesTags: (_result, error, { chatId }) =>
          error ? [] : [{ type: "GroupSettings" as const, id: `CHAT_${chatId}` }]
      }),

      setGroupDefaultCardStyle: builder.mutation<SetGroupDefaultCardStyleResponse, SetGroupDefaultCardStyleRequest>({
        queryFn: chatIdMutation<SetGroupDefaultCardStyleRequest, SetGroupDefaultCardStyleResponse>((chatId, arg) => ({
          url: `/api/v1/groups/${chatId}/default-card-style`,
          method: "PUT",
          body: { style_id: arg.styleId }
        })),
        invalidatesTags: (_result, error, { chatId }) =>
          error ? [] : [{ type: "GroupSettings" as const, id: `CHAT_${chatId}` }, "UserCardStyle", "CardStyles"]
      }),

      updateMediaRestrictions: builder.mutation<UpdateMediaRestrictionsResponse, UpdateMediaRestrictionsRequest>({
        queryFn: chatIdMutation<UpdateMediaRestrictionsRequest, UpdateMediaRestrictionsResponse>((chatId, arg) => ({
          url: `/api/v1/group/rating/restrictions?chat_id=${chatId}`,
          method: "PUT",
          body: arg
        })),
        invalidatesTags: (_result, error, { chatId }) =>
          error ? [] : [{ type: "MediaRestrictions" as const, id: `CHAT_${chatId}` }]
      }),

      updateNotificationPreferences: builder.mutation<
        UpdateNotificationPreferencesResponse,
        UpdateNotificationPreferencesRequest
      >({
        queryFn: chatIdMutation<UpdateNotificationPreferencesRequest, UpdateNotificationPreferencesResponse>(
          (chatId, arg) => ({ url: `/api/v1/group/rating/notifications?chat_id=${chatId}`, method: "PUT", body: arg })
        ),
        invalidatesTags: (_result, error, { chatId }) =>
          error ? [] : [{ type: "NotificationPreferences" as const, id: `CHAT_${chatId}` }]
      })
    })
  })

export const {
  useUpdateGroupSettingsMutation,
  useSetGroupDefaultCardStyleMutation,
  useUpdateMediaRestrictionsMutation,
  useUpdateNotificationPreferencesMutation
} = updateGroupSettingsApi
